import {t, Trans} from '@lingui/macro'
import type {MouseEventHandler, ReactNode} from 'react'
import styled from 'styled-components'

import {Box, Button, Popover, spacing, tokens, Text} from '@pleo-io/telescope'

import type {BillingInterval} from '~/api/deimos/billing'
import {Interval} from '~/api/deimos/billing'
import type {PlanType} from '~/api/types/plan'
import {getFeatureCountryConfiguration} from '~/country-configuration/configuration'
import {Feature} from '~/country-configuration/types'
import {reportError} from '~/error/report'
import {formatCurrency} from '~/locale/currency'
import {getCountry, useUser} from '~/shell/user'
import tracking from '~/tracking'

import {FallbackAdditionalPricing} from './fallback-additional-pricing'
import {getUserPricingTable} from './pricing-plan-table'
import type {AdditionalUsersBulkSizes} from './types'

const PopoverContainer = styled(Box)`
    padding: ${spacing.spacing24};
    width: 288px;
    height: 100%;
`
const PopoverContentContainer = styled(Box)`
    padding: ${spacing.spacing10};

    display: flex;
    flex-direction: row;
    justify-content: space-between;

    border-bottom: 1px solid ${tokens.shade300};

    &:first-child {
        border-bottom: 0;
    }

    &:last-child {
        border-bottom: 0;
    }
`

interface AdditionalUserPricingProps {
    planType?: PlanType
    planInterval?: Interval
    planCurrency?: string
    billingInterval?: BillingInterval
    alternativeLabel?: ReactNode
}

const translatedAmountOfUsers = (users: AdditionalUsersBulkSizes) =>
    users === '>600'
        ? t({
              message: `Above 600`,
          })
        : users

export const AdditionalUserPricing = ({
    planType,
    planInterval,
    planCurrency,
    billingInterval,
    alternativeLabel,
}: AdditionalUserPricingProps) => {
    const user = useUser()
    const country = getCountry(user)

    const {isCashBackCountry} = getFeatureCountryConfiguration(Feature.Paywall, country)

    const userPricing = getUserPricingTable(
        planInterval === Interval.YEAR,
        planType,
        billingInterval,
        planCurrency,
    )

    const needMoreClicked: MouseEventHandler<HTMLButtonElement> = () => {
        tracking.pricingEnquiryLinkActioned({
            //eslint-disable-next-line string-to-lingui/missing-lingui-transformation
            location: `${planType} plan user limit`,
            destination: 'POPOVER',
            source: window.location.href,
        })
    }

    if (!userPricing) {
        reportError(
            undefined,
            'Could not show additional user prices. Falling back to website redirect',
            {planType, planInterval, country, isCashBackCountry},
        )
        return <FallbackAdditionalPricing planType={planType} />
    }

    return (
        <Popover>
            <Popover.Trigger type="button">
                <Button variant="link" data-testid="need-more-button" onClick={needMoreClicked}>
                    {alternativeLabel ? alternativeLabel : <Trans>Need more?</Trans>}
                </Button>
            </Popover.Trigger>
            <Popover.Content side="right">
                <PopoverContainer>
                    <PopoverContentContainer>
                        <Text weight="medium">
                            <Trans>Additional users</Trans>
                        </Text>

                        <Text weight="medium">
                            <Trans>Price</Trans>
                        </Text>
                    </PopoverContentContainer>

                    {userPricing?.map((data, index) => {
                        return (
                            <PopoverContentContainer
                                key={index}
                                data-testid={`addition-users-${data.amountOfUsers}`}
                            >
                                <Text variant="medium-default">
                                    {translatedAmountOfUsers(data.amountOfUsers)}
                                </Text>
                                <Text variant="medium-default">
                                    {formatCurrency(data.price, planCurrency, {
                                        format: {
                                            minimumFractionDigits: 0,
                                            maximumFractionDigits: 1,
                                        },
                                    })}
                                </Text>
                            </PopoverContentContainer>
                        )
                    })}
                </PopoverContainer>
                <Popover.Arrow />
            </Popover.Content>
        </Popover>
    )
}
